import { processEnv } from './react-env'

/**
 * Resolves the LaunchDarkly client ID based on the current browser domain.
 *
 * The REACT_APP_LD_CLIENTID can be either:
 * - A JSON object mapping domain → client ID: `{"qa-stg-match.optum.com":"123", "qa-match.optum.com":"234"}`
 * - A fallback string for backward compatibility or direct values
 *
 * @returns The client ID for the current domain, or empty string if not found
 */
export const getLaunchDarklyClientId = (): string => {
  const ldClientIdValue = processEnv().LD_CLIENTID

  if (!ldClientIdValue) {
    return ''
  }

  try {
    // Try to parse as JSON object (domain mapping)
    const domainMap = JSON.parse(ldClientIdValue) as Record<string, string>

    // If it's an object (not a string), look up the current domain
    if (
      typeof domainMap === 'object' &&
      domainMap !== null &&
      !Array.isArray(domainMap)
    ) {
      const currentDomain = window.location.hostname
      const clientId = domainMap[currentDomain]

      if (clientId) {
        return clientId
      }

      // Try to find a match by checking if current domain ends with any configured domain
      for (const configuredDomain in domainMap) {
        if (
          configuredDomain !== 'default' &&
          currentDomain.endsWith(configuredDomain)
        ) {
          return domainMap[configuredDomain]
        }
      }

      // Check for default fallback
      if (domainMap['default']) {
        return domainMap['default']
      }

      // Log warning if domain is not in mapping and no default exists
      // eslint-disable-next-line no-console
      console.warn(
        `[LaunchDarkly] Current domain "${currentDomain}" not found in LD_CLIENTID mapping. Available domains: ${Object.keys(domainMap).join(', ')}`
      )
      return ''
    }
  } catch {
    // If JSON parsing fails, treat it as a direct string value (backward compatibility)
    return ldClientIdValue
  }

  return ''
}
